/**
 * Results Table Component
 * 4-column table for classification results
 */
import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  LinearProgress,
} from '@mui/material';
import {
  Download as DownloadIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  MoreVert as MoreIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { ClassificationResult } from '../../../services/tariffClassifierPageService';

interface ResultsTableProps {
  results: ClassificationResult[];
  transactionId: string;
  loading?: boolean;
  onDownload: (format: 'csv' | 'excel') => void;
  downloadLoading?: boolean;
}

const ResultsTable: React.FC<ResultsTableProps> = ({
  results,
  transactionId,
  loading = false,
  onDownload,
  downloadLoading = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [copiedRow, setCopiedRow] = useState<number | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDownload = (format: 'csv' | 'excel') => {
    onDownload(format);
    handleMenuClose();
  };

  const handleCopyHSCode = (hsCode: string, rowNumber: number) => {
    navigator.clipboard.writeText(hsCode);
    setCopiedRow(rowNumber);
    setTimeout(() => setCopiedRow(null), 2000);
  };

  const getConfidenceColor = (confidence: number): 'success' | 'warning' | 'error' => {
    if (confidence >= 0.9) return 'success';
    if (confidence >= 0.75) return 'warning';
    return 'error';
  };

  const formatHSCode = (hsCode: string): string => {
    if (!hsCode) return '';
    const clean = hsCode.replace(/[\s.-]/g, '');
    if (clean.length === 8) {
      return `${clean.slice(0, 2)}.${clean.slice(2, 4)}.${clean.slice(4, 6)}.${clean.slice(6, 8)}`;
    }
    return hsCode;
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Processing classifications...
        </Typography>
      </Box>
    );
  }

  if (results.length === 0) {
    return (
      <Alert severity="info">
        No results to display. Upload a file or use direct classification.
      </Alert>
    );
  }

  const successCount = results.filter((r) => r.status === 'success').length;
  const failureCount = results.length - successCount;
  const avgConfidence =
    results.reduce((sum, r) => sum + (r.confidence || 0), 0) / results.length;

  return (
    <Box>
      {/* Statistics Bar */}
      <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.light' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Total Items
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {results.length}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Successful
              </Typography>
              <Typography variant="h6" fontWeight={700} color="success.main">
                {successCount}
              </Typography>
            </Box>
            {failureCount > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Failed
                </Typography>
                <Typography variant="h6" fontWeight={700} color="error.main">
                  {failureCount}
                </Typography>
              </Box>
            )}
            <Box>
              <Typography variant="caption" color="text.secondary">
                Avg. Confidence
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {(avgConfidence * 100).toFixed(1)}%
              </Typography>
            </Box>
          </Box>

          {/* Download Button */}
          <Box>
            <Button
              variant="contained"
              startIcon={downloadLoading ? <CircularProgress size={16} /> : <DownloadIcon />}
              onClick={handleMenuOpen}
              disabled={downloadLoading}
            >
              Download Results
            </Button>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
              <MenuItem onClick={() => handleDownload('csv')}>Download as CSV</MenuItem>
              <MenuItem onClick={() => handleDownload('excel')}>Download as Excel</MenuItem>
            </Menu>
          </Box>
        </Box>
      </Paper>

      {/* Results Table */}
      <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 700 }}>#</TableCell>
              <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 700, minWidth: 250 }}>
                Original Description
              </TableCell>
              <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 700, minWidth: 120 }}>
                Original HS Code
              </TableCell>
              <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 700, minWidth: 150 }}>
                Generated HS Code
              </TableCell>
              <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 700, minWidth: 120 }}>
                Confidence
              </TableCell>
              <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 700 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {results.map((result, index) => (
              <TableRow
                key={index}
                sx={{
                  '&:hover': { bgcolor: 'action.hover' },
                  bgcolor: result.status === 'failed' ? 'error.light' : 'inherit',
                }}
              >
                <TableCell>{result.row_number || index + 1}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                    {result.original_description || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {result.original_hs_code ? (
                    <Chip
                      label={formatHSCode(result.original_hs_code)}
                      size="small"
                      variant="outlined"
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Not provided
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {result.generated_hs_code ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={formatHSCode(result.generated_hs_code)}
                        size="small"
                        color="primary"
                      />
                      <Tooltip
                        title={
                          copiedRow === result.row_number ? 'Copied!' : 'Copy HS Code'
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleCopyHSCode(
                              result.generated_hs_code,
                              result.row_number
                            )
                          }
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Not generated
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {result.confidence !== undefined && result.confidence !== null ? (
                    <Chip
                      label={`${(result.confidence * 100).toFixed(0)}%`}
                      size="small"
                      color={getConfidenceColor(result.confidence)}
                    />
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>
                  {result.status === 'success' ? (
                    <Chip
                      label="Success"
                      size="small"
                      color="success"
                      icon={<SuccessIcon />}
                    />
                  ) : (
                    <Chip label="Failed" size="small" color="error" icon={<ErrorIcon />} />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Download Progress */}
      {downloadLoading && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            Preparing download...
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ResultsTable;
